import type { Guid } from 'guid-typescript';
import type { Router, RouteLocationNormalized } from 'vue-router';
import { Common } from '@/common/Common';
import type { IDictionaryItem } from '@/models/common/DictionaryItem';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { useApi } from '@/common/ApiHelper';
import { EntityCache } from '@sysarb/sysarb-api/cache';
import type { IAddedEvent, IDataSourceChangedEvent, IDeletedEvent, IDuplicatedEvent, IEvent, IFilterUpdatedEvent, IMassiveChangeEvent, IMethodVisibilityEvent, INotificationEvent, IProgressEvent, IPropertyUpdatedEvent, IQuestionEvent, IRoleChangedEvent, IVisibilityEvent } from '@sysarb/sysarb-api/models/common/event';
import type ISubscriptionOptions from '@sysarb/sysarb-api/models/common/SubscriptionOptions';
import { storeToRefs } from 'pinia';

const EventServiceInterval = 5000;

export class EventService {
    private static instance: EventService | null = null;

    public static getInstance(router: Router | null): EventService {
        EventService.instance ??= new EventService(router);
        return EventService.instance;
    }

    /** Reference to the current set timer. */
    private timer: number | null = null;

    /** What interval on when the timer should trigger. */
    private readonly interval: number;

    /** Reference to the current subscription timer. */
    private waitingSubscriptionTimer!: number;

    /** List of waiting subscriptions. */
    public waitingSubscriptions: ISubscriptionOptions[] = [];

    /** List of subscriptions made connected to what component made it. */
    public subscriptions: ISubscriptionOptions[] = [];

    /** Used if needed to route the user to login. */
    private readonly $router!: Router;

    /** The current application route. Is updated from the outside. */
    public route!: RouteLocationNormalized;

    public constructor(router: Router | null) {
        this.interval = EventServiceInterval;
        if (router != null) {
            this.$router = router;
        }
    }

    /**
     * Gets events and restarts the timer.
     */
    public async getEventsWithTimer(): Promise<void> {
        const { isLoggedIn, selectedInstance } = storeToRefs(useLoginStore());

        // Don't get events if not logged in or on login page
        if (!isLoggedIn.value || selectedInstance.value == null) {
            // Continue timer
            this.startEventTimer();
            return Promise.resolve();
        }

        const api = useApi();

        return api.system.getEvents().then((events: IEvent[]) => {
            this.handleEvents(events);
            this.startEventTimer();
        }).catch((reason: unknown) => {
            console.error('error', reason);
            this.startEventTimer();
        });
    }

    /**
     * Gets events.
     */
    public async getEvents(): Promise<void> {
        const { isLoggedIn } = storeToRefs(useLoginStore());
        if (!isLoggedIn.value) return;

        const api = useApi();

        try {
            const events: IEvent[] = await api.system.getEvents();
            this.handleEvents(events);
        } catch (reason: unknown) {
            console.error('error', reason);
        }
    }

    /**
     * Handles the currently fetched events.
     * Goes through the relevant subscriptions and triggers callback functions.
     */
    private handleEvents(events?: IEvent[]): void {
        if (events == null || events.length === 0) {
            return;
        }

        // Group events by their entityname and type.
        const eventGroups: Map<string, IEvent[]> = new Map();
        events.forEach(event => {
            const key = JSON.stringify({ name: event.entityName, type: event.$type! });
            if (eventGroups.has(key)) {
                eventGroups.get(key)!.push(event);
            } else {
                eventGroups.set(key, [event]);
            }
        });

        // Filter subscriptions to be used on bulkPropertyUpdateCallback and of type PropertyUpdatedEvent.
        eventGroups.forEach((eventGroup, key) => {
            const keyObject = JSON.parse(key);
            const subscriptions = this
                .subscriptions
                .filter(x => x.entity === keyObject.name && x.id == null);

            subscriptions.forEach(subscription => {
                switch (keyObject.type) {
                    case 'MassiveChangeEvent':
                        if (subscription.bulkMassiveChangeCallback != null && keyObject.type === 'MassiveChangeEvent') {
                            subscription.bulkMassiveChangeCallback(eventGroup as IMassiveChangeEvent[]);
                        }
                        break;
                    case 'PropertyUpdatedEvent':
                        if (subscription.bulkPropertyUpdatedCallback != null && keyObject.type === 'PropertyUpdatedEvent') {
                            subscription.bulkPropertyUpdatedCallback(eventGroup as IPropertyUpdatedEvent[]);
                        }
                        break;
                    default:
                        break;
                }
            });
        });
        events.forEach(event => {
            this.updateCache(event);

            // RoleChangedEvents doesn't need a subscription to trigger.
            if (event.$type === 'RoleChangedEvent') return;

            // Filter relevant subscriptions
            const subscriptions = this
                .subscriptions
                .filter(x => x.entity === event.entityName && (x.id == null || event.entityID.isEqualTo(x.id)));

            // Trigger callbacks on all subscriptions based on event type
            subscriptions.forEach(subscription => {
                switch (event.$type) {
                    case 'AddedEvent':
                        if (subscription.addedCallback != null) {
                            subscription.addedCallback(event as IAddedEvent);
                        }
                        break;
                    case 'DataSourceChangedEvent':
                        if (subscription.dataSourceChangedCallback != null) {
                            subscription.dataSourceChangedCallback(event as IDataSourceChangedEvent);
                        }
                        break;
                    case 'DeletedEvent':
                        if (subscription.deletedCallback != null) {
                            subscription.deletedCallback(event as IDeletedEvent);
                        }
                        break;
                    case 'DuplicatedEvent':
                        if (subscription.duplicatedCallback != null) {
                            subscription.duplicatedCallback(event as IDuplicatedEvent);
                        }
                        break;
                    case 'FilterUpdatedEvent':
                        if (subscription.filterUpdatedCallback != null) {
                            subscription.filterUpdatedCallback(event as IFilterUpdatedEvent);
                        }
                        break;
                    case 'MassiveChangeEvent':
                        if (subscription.massiveChangeCallback != null) {
                            subscription.massiveChangeCallback(event as IMassiveChangeEvent);
                        } else {
                            console.info('EventService', `Unhandled massive change event on ${subscription.entity}`);
                        }
                        break;
                    case 'MethodVisibilityEvent':
                        if (subscription.methodVisibilityCallback != null) {
                            subscription.methodVisibilityCallback(event as IMethodVisibilityEvent);
                        }
                        break;
                    case 'NotificationEvent':
                        if (subscription.notificationCallback != null) {
                            subscription.notificationCallback(event as INotificationEvent);
                        }
                        break;
                    case 'ProgressEvent':
                        if (subscription.progressCallback != null) {
                            subscription.progressCallback(event as IProgressEvent);
                        }
                        break;
                    case 'PropertyUpdatedEvent':
                        if (subscription.propertyUpdatedCallback != null) {
                            const propertyUpdatedEvent = event as IPropertyUpdatedEvent;
                            propertyUpdatedEvent.propertyName = Common.toLowerCamelCase(propertyUpdatedEvent.propertyName);
                            subscription.propertyUpdatedCallback(propertyUpdatedEvent);
                        }
                        break;
                    case 'QuestionEvent':
                        if (subscription.questionCallback != null) {
                            subscription.questionCallback(event as IQuestionEvent);
                        }
                        break;
                    case 'RoleChangedEvent':
                        if (subscription.roleChangedCallback !== undefined) {
                            subscription.roleChangedCallback(event as IRoleChangedEvent);
                        }
                        break;
                    case 'VisibilityEvent':
                        if (subscription.visibilityCallback != null) {
                            subscription.visibilityCallback(event as IVisibilityEvent);
                        }
                        break;
                    default:
                        break;
                }
            });
        });
    }

    /**
     * Updates the cached entities based on event type
     * @param event Event to handle
     */
    private updateCache(event: IEvent): void {
        switch (event.$type) {
            case 'AddedEvent': {
                const addedEvent = event as IAddedEvent;
                EntityCache.remove(event.entityID);
                EntityCache.add(event.entityID, Promise.resolve(addedEvent.entity));
                break;
            }
            case 'DeletedEvent':
                EntityCache.remove(event.entityID);
                break;
            case 'PropertyUpdatedEvent': {
                const propertyUpdatedEvent = event as IPropertyUpdatedEvent;

                void EntityCache.updateProperty(
                    event.entityID,
                    Common.toLowerCamelCase(propertyUpdatedEvent.propertyName),
                    propertyUpdatedEvent.propertyValue
                );

                break;
            }
            case 'MassiveChangeEvent':
                EntityCache.clear();
                break;
            default:
                break;
        }
    }

    /**
     * Subscribe a component to an entities events.
     * This function makes sure to handle calls within 500 ms as a group
     * @param subscription The subscripcion options to use.
     */
    public subscribe(subscription: ISubscriptionOptions): void {
        // Search for existing subscription
        const existingSubscription = this.getExistingSubscription(subscription);

        // If subscription is found call subscribeRun directly, else wait for more subscriptions and make group call if neccessary
        if (existingSubscription != null) {
            void this.subscribeRun(subscription);
        } else {
            // Add new subscription to waiting for subscription
            this.waitingSubscriptions.push(subscription);

            // Clear timeout (this makes sure that subscriptions within timeout executes at same time)
            window.clearTimeout(this.waitingSubscriptionTimer);

            // Starts a timeout to collect all subscriptions executed within 500 ms
            this.waitingSubscriptionTimer = window.setTimeout(
                () => {
                    // Grouping subscriptions based on entity
                    const subscriptionGroups = this.waitingSubscriptions.reduce(
                        (groups: IDictionaryItem<string, ISubscriptionOptions[]>[], item: ISubscriptionOptions) => {
                            const existingGroup = groups.find(x => x.key === item.entity);
                            if (existingGroup != null) {
                                existingGroup.data.push(item);
                            } else {
                                groups.push({ key: item.entity, data: [item] });
                            }
                            return groups;
                        },
                        []
                    );

                    // Clear waiting for subscription
                    this.waitingSubscriptions = [];

                    // Handle the subscription groups
                    subscriptionGroups.forEach((group: IDictionaryItem<string, ISubscriptionOptions[]>) => {
                        // Check if any of the subscriptions calls without id (then use this first else just pick first data in array)
                        const noID = group.data.findIndex(x => x.id == null);
                        let firstData: ISubscriptionOptions;
                        if (noID !== -1) {
                            [firstData] = group.data.splice(noID, 1);
                        } else {
                            firstData = group.data.shift() as ISubscriptionOptions;
                        }

                        // Handle subscription, first data runs first then we run the rest (this is done to prevent unnecessary api calls)
                        void this.subscribeRun(firstData).then(() => {
                            group.data.forEach(async data => this.subscribeRun(data));
                        });
                    });
                },
                1000 // Timeout
            );
        }
    }

    /**
     * Subscribe a component to an entities events.
     * @param subscription The subscription options to use.
     */
    public async subscribeRun(subscription: ISubscriptionOptions): Promise<void> {
        const { isLoggedIn } = storeToRefs(useLoginStore());
        if (!isLoggedIn.value) return;

        // Search for existing subscription
        const existingSubscription = this.getExistingSubscription(subscription);
        const api = useApi();

        // If no existing subscription is found create subscription on server
        if (existingSubscription == null) {
            const id = await api.system.subscribe({
                EntityID: subscription.id ?? null,
                EntityName: subscription.entity
            });
            subscription.subscriptionId = id;
        } else {
            // Connect new subscription with already existing subscription match
            subscription.subscriptionId = existingSubscription.subscriptionId;
        }

        // Add new subscription to subscriptions list
        this.subscriptions.push(subscription);
    }

    /**
     * Unsubscripe all events
     */
    public async unsubscribe(): Promise<void> {
        const api = useApi();
        try {
            await api.system.unsubscribeAll();
        } catch { /* NOTHING */ }
    }

    /**
     * Removes subscriptions on events connected to the current component id.
     * @param componentId The unique compoment's id to unsubscribe events on.
     */
    public unsubscribeComponent(componentId: Guid): void {
        this.subscriptions = this.subscriptions.filter((value, index, array) => {
            // Determine if this subscription should be removed
            const removeThis = value?.componentId?.isEqualTo(componentId) ?? false;

            // Check if other subscriptions share this subscription on server
            const shareSubscription = array.some(x => x.subscriptionId != null && value.subscriptionId != null && x.subscriptionId.isEqualTo(value.subscriptionId));

            // Unsubscribe from server if this should be removed
            if (removeThis && value.subscriptionId != null && !shareSubscription) {
                const api = useApi();
                void api.system.unsubscribe(value.subscriptionId);
            }

            // Return true if this subscription should not be removed
            return !removeThis;
        });
    }

    /** Starts the event timer to get events */
    public startEventTimer(): void {
        this.timer = window.setTimeout(async () => this.getEventsWithTimer(), this.interval);
    }

    /** Stops the event timer to get events */
    public endEventTimer(): void {
        if (this.timer != null) {
            window.clearTimeout(this.timer);
        }
    }

    private getExistingSubscription(subscription: ISubscriptionOptions): ISubscriptionOptions | undefined {
        return this.subscriptions.find(x => x.entity === subscription.entity && (x.id == null || x.id?.toString() === subscription.id?.toString()));
    }
}
